// ── Wedding Party page (/party) ───────────────────────────────────────────────
// Everything shown on the wedding-party page lives here so it's easy to update
// without touching the component. Times come from the planning calls with
// Logan (Sep 2) and Lindsey (Oct 5); Logan's final timeline wins if anything
// shifts. Roles and pairings are from Caroline's questionnaire to Saint William.

const MAPS = {
  church:  'https://maps.google.com/?q=601+Seagate+Dr+Naples+FL+34108',
  collier: 'https://maps.google.com/?q=The+Old+Collier+Golf+Club+Naples+FL',
  laplaya: 'https://maps.google.com/?q=9891+Gulf+Shore+Dr+Naples+FL+34108',
  nbc:     'https://maps.google.com/?q=801+Gulf+Shore+Blvd+N+Naples+FL+34102',
};

const PARTY_INFO = {
  intro: {
    title: 'The Wedding Party',
    tagline: "Your home base for the weekend — where to be, when, and who's who.",
    note: "Times are our current plan and could shift a little. Logan will send the final timeline wedding week, and we'll keep this page updated.",
  },

  // Three at-a-glance cards under the title
  keyMoments: [
    { day: 'Wed · Oct 28', title: 'Church rehearsal', time: '4:00 PM', place: 'Saint William', note: 'Rehearsal dinner follows at 5:30 PM at The Old Collier Golf Club' },
    { day: 'Thu · Oct 29', title: 'Welcome Party',    time: '6:00 PM', place: 'LaPlaya Beach Club', note: 'Coastal cocktail · outdoors on grass' },
    { day: 'Fri · Oct 30', title: 'Bus to the church', time: '12:50 PM', place: 'Naples Beach Club', note: 'Hair & makeup wraps by noon · Mass at 3:00 PM' },
  ],

  // Wedding-party timeline — same shape the Schedule page uses
  days: [
    {
      num: '28', weekday: 'Wednesday', month: 'October 2026',
      events: [
        {
          time: '4:00 PM', end: '5:00 PM', name: 'Church Rehearsal',
          venue: 'Saint William Catholic Church', venueLink: MAPS.church, address: '601 Seagate Dr, Naples, FL 34108',
          notes: "Everyone with a part in the Mass: the wedding party, ushers, readers, gift bearers, parents, and the kids. We'll walk through the processional and the exit — plan on a solid hour.",
        },
        {
          time: '5:30 PM', name: 'Rehearsal Dinner',
          venue: 'The Old Collier Golf Club', venueLink: MAPS.collier, address: 'Naples, FL',
          notes: 'Straight from the church — dinner starts at 5:30.',
        },
      ],
    },
    {
      num: '29', weekday: 'Thursday', month: 'October 2026',
      events: [
        {
          time: '6:00 PM', end: '8:30 PM', name: 'Welcome Party',
          venue: 'LaPlaya Beach Club', venueLink: MAPS.laplaya, address: '9891 Gulf Shore Dr, Naples, FL 34108', attire: 'Coastal Cocktail',
          notes: "Caroline, Michael and immediate family head over around 5:00 for a few photos — the wedding party just comes at 6. It's outdoors on grass, so plan footwear accordingly. Transportation runs to and from the Naples Beach Club, and valet is complimentary if you drive.",
        },
      ],
    },
    {
      num: '30', weekday: 'Friday', month: 'October 2026',
      events: [
        {
          time: 'Morning', end: 'done by 12:00 PM', name: 'Hair, Makeup & Getting Ready',
          venue: 'Naples Beach Club, A Four Seasons Resort', venueLink: MAPS.nbc, address: '801 Gulf Shore Blvd N, Naples, FL 34102',
          bulletNotes: [
            'Girls: hair and makeup wraps by noon so there is time to get into dresses. Your individual time slot and the suite number will come by text.',
            'Guys: dressed and down in the lobby by 12:45 PM.',
          ],
        },
        {
          time: '12:50 PM', name: 'Bus Departs for Saint William',
          venue: 'Naples Beach Club, A Four Seasons Resort', venueLink: MAPS.nbc, address: null,
          notes: 'The whole wedding party — girls and guys — rides together. Caroline and her dad follow in the hotel car at 1:00. Please don\'t drive yourself; shuttles bring everyone back after Mass.',
        },
        {
          time: '1:15 PM', end: '2:10 PM', name: 'First Look & Wedding Party Photos',
          venue: 'Saint William Catholic Church', venueLink: MAPS.church, address: '601 Seagate Dr, Naples, FL 34108',
          notes: "Caroline and Michael's first look in the church gardens, then wedding party photos with Lindsey. The flower girls and ring bearers arrive around 2:00 so they aren't in their outfits too long.",
        },
        {
          time: '3:00 PM', end: '4:00 PM', name: 'Wedding Mass',
          venue: 'Saint William Catholic Church', venueLink: MAPS.church, address: null, attire: 'Black Tie',
          notes: 'A full Mass, about an hour. After the final blessing everyone lines up outside for the send-off — Caroline and Michael leave in a vintage car.',
        },
        {
          time: null, name: 'Between Mass & Cocktail Hour', subtle: true,
          bulletNotes: [
            'Immediate-family photos first at the church, then shuttles back to the Naples Beach Club.',
            'Caroline and Michael take a few photos around the resort before cocktail hour.',
            "Grab a drink at Sunset Bar, HB's, or the Merchant Room while you wait.",
          ],
        },
        {
          time: '5:30 PM', end: '10:30 PM', name: 'Cocktail Hour, Dinner & Dancing',
          venue: 'Naples Beach Club, A Four Seasons Resort', venueLink: MAPS.nbc, address: '801 Gulf Shore Blvd N, Naples, FL 34102', attire: 'Black Tie',
          notes: "Cocktail hour on the Palm Lawn (grass again!), then dinner and dancing in the Poinciana Ballroom. Toasts and Father Tom's blessing come before the first dances — if you're giving a toast, we'll confirm timing with you.",
        },
        {
          time: '10:30 PM', end: '12:30 AM', name: 'After Party',
          venue: 'The Wager', venueLink: 'https://www.fourseasons.com/naplesbeachclub/dining/restaurants/wager-sports-pub/', address: 'Naples Beach Club, A Four Seasons Resort — next door to the reception',
          notes: "Keep the night going at The Wager, the resort's sports pub right next door to the reception — bowling lanes, putting on the green outside, late-night food, and plenty of games (pop-a-shot, pool, Golden Tee).",
        },
      ],
    },
  ],

  // Who's who
  roster: {
    bride: [
      { role: 'Matron of Honor', names: ['Julie Leeney'] },
      { role: 'Maid of Honor',   names: ['Stephanie Dussias'] },
      { role: 'Bridesmaids',     names: ['Ann Marie Wernick', 'Stephanie Ferry', 'Laura Ferry', 'Christine Kalt', 'Lauren Comfort', 'Colleen Manner', 'Caroline Stadler', 'Elizabeth Sullivan', 'Kaira LaBarbera'] },
    ],
    groom: [
      { role: 'Best Men',   names: ['Brian Ferry', 'Daniel Hennessy'] },
      { role: 'Groomsmen',  names: ['Joseph Kalt', 'Samuel Wernick', 'John Comfort', 'Derek Leeney'] },
    ],
    ceremony: [
      { role: 'Ushers & Programs',     names: ['Charles Walsh', 'James Donahue', 'Evan Shuck', 'Anthony Ertle', 'Garrett Joseph', 'Matthew Ivancic', 'John Thomas'] },
      { role: 'Readers',               names: ['Erin Magee — First Reading (Proverbs 3:1–6)', 'Sean Hennessy — Second Reading (Colossians 3:12–17)'] },
      { role: 'Prayers of the Faithful', names: ['Scott Arthrell', 'Bennett Kinzel'] },
      { role: 'Gift Bearers',          names: ['Adrienne Ashworth', 'Claire Haley'] },
      { role: 'Flower Girls & Ring Bearers', names: ['Our eleven little ones — arriving at the church around 2:00 PM'] },
    ],
    escorts: [
      'Joan Comfort, escorted by John Comfort',
      'Kathleen Ferry, escorted by Michael',
      'Mary Lou Durkin (Grandma), escorted by Brian and Michael',
    ],
    // Processional — siblings Brian & Julie first, then each groomsman with two bridesmaids
    processional: [
      ['Julie Leeney', 'Brian Ferry'],
      ['Stephanie Dussias', 'Daniel Hennessy', 'Colleen Manner'],
      ['Lauren Comfort', 'John Comfort', 'Caroline Stadler'],
      ['Christine Kalt', 'Joseph Kalt', 'Laura Ferry'],
      ['Elizabeth Sullivan', 'Derek Leeney', 'Kaira LaBarbera'],
      ['Ann Marie Wernick', 'Samuel Wernick', 'Stephanie Ferry'],
    ],
  },

  attire: [
    { label: 'Thursday · Welcome Party', text: 'Coastal Cocktail — sundresses or colorful cocktail dresses; polos, button-downs or light sport jackets for the guys. Grass underfoot, so skip the stilettos.' },
    { label: 'Friday · Mass & Reception', text: 'Black Tie — floor-length dresses and tuxedos. Cocktail hour is on the lawn, so bring shoes you can stand in on grass.' },
    { label: 'Bridesmaids', text: 'Dress, shoe and accessory details are coming from Caroline directly.' },
    { label: 'Groomsmen', text: 'Tux, shirt and shoe details are coming from Michael directly.' },
  ],

  faqs: [
    { q: 'Do I need to be at the rehearsal on Wednesday?',
      a: 'Yes, please — if you are in the wedding party, an usher, a reader, a gift bearer, or a parent, we need you at Saint William at 4:00 PM. It runs about an hour, and then everyone heads to The Old Collier Golf Club for dinner at 5:30.' },
    { q: 'How do I get to the church on Friday?',
      a: 'A bus leaves the Naples Beach Club at 12:50 PM with the whole wedding party, girls and guys together. Caroline and her dad follow in the hotel car at 1:00. Please don\'t drive — shuttles run from Saint William back to the resort after Mass.' },
    { q: 'How long is the Mass?',
      a: 'It starts at 3:00 PM and is a full Catholic Mass, so plan on about an hour. You will already be at the church for photos, so no need to worry about arriving early.' },
    { q: 'Where do we get ready?',
      a: 'At the Naples Beach Club. Hair and makeup for the girls wraps by noon so there is time to get dressed; your time slot and the suite number will come by text. Guys, be dressed and in the lobby by 12:45.' },
    { q: 'What is the dress code?',
      a: '<b>Thursday:</b> Coastal Cocktail. <b>Friday:</b> Black Tie for the Mass and the reception. Both the Welcome Party and cocktail hour are outdoors on grass — plan your footwear accordingly!' },
    { q: 'Where should I stay?',
      a: 'The room block is at the Naples Beach Club, A Four Seasons Resort (promo code 261001CCW, or call (239) 944-7600). Weekend shuttles run only to and from the Naples Beach Club, so staying there makes everything easier.' },
    { q: 'What happens between the Mass and cocktail hour?',
      a: 'Immediate-family photos at the church first, then shuttles back to the resort. Cocktail hour starts at 5:30 on the Palm Lawn — until then, Sunset Bar, HB\'s and the Merchant Room are all open.' },
    { q: 'Is there an after party?',
      a: 'Yes — from 10:30 PM to 12:30 AM at <a href="https://www.fourseasons.com/naplesbeachclub/dining/restaurants/wager-sports-pub/" target="_blank" rel="noopener noreferrer">The Wager</a>, the sports pub right next door to the reception. Bowling lanes, putting on the green outside, late-night food, and plenty of games — pop-a-shot, pool, Golden Tee.' },
    { q: 'Who do I contact if something comes up?',
      a: 'Before the weekend, text Caroline or Michael. On the day, Logan Paradiso and her team are running the show — see Contacts below.' },
  ],

  contacts: [
    { name: 'Logan Paradiso', role: 'Wedding planner — your day-of point of contact', email: 'hello@loganparadisoevents.com' },
    { name: 'Katie Marcoguiseppe', role: 'Paradiso Events', email: 'katie.lpevents@gmail.com' },
    { name: 'Caroline & Michael', role: 'Anything before the weekend — just text us', email: null },
  ],
};

export default PARTY_INFO;
